import { withRouter } from "react-router-dom";
import { withNamespaces } from "react-i18next";
import { connect } from "react-redux";
import { AvField, AvForm } from "availity-reactstrap-validation";
import { Button, Card, CardBody, Col, Label, Row, CardHeader, Alert } from "reactstrap";
import { useCallback, useContext, useMemo, useState } from "react";
import ProductContext from "../../../../contexts/ProductContext";
import GeoMap from "src/modules/3rd-party-management/components/geo-map";
import { useMutation, useQuery } from "@tanstack/react-query";
import SupplierProductService from "src/modules/3rd-party-management/apis/common/SupplierProductService";
import CommonService from "src/modules/3rd-party-management/apis/common/CommonService";
import { toast } from "react-toastify";
import { RiErrorWarningLine, RiMapPinLine } from "react-icons/ri";

const Geolocation = ({
    t,
    onNextButtonClicked,
    onBackButtonClicked,
    onCancelButtonClicked
}) => {
    const {
        productData,
        setProductData
    } = useContext(ProductContext);

    const commodity = (productData.name?.commodity || '').toLowerCase();

    const [geoLocationData, setGeoLocationData] = useState({
        geolocationReference: [],
        farmGeolocationReference: [],
        slaughterGeolocationReference: []
    });

    const {
        mutateAsync: validatePlotMutation
    } = useMutation({
        mutationFn: async (payload) => {
            const service = SupplierProductService.getInstance();
            return await service.validateGeoLocation(payload);
        }
    });

    const {
        data: originCountryGeoData,
        isFetching: originCountryGeoDataIsFetching
    } = useQuery({
        queryKey: [
            '3rd-party-management-fetch-country-geo-data',
            productData.originCountry?.value
        ],
        queryFn: async () => {
            const service = CommonService.getInstance();

            return await service.fetchCountryGeoData(productData.originCountry?.value);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        enabled: !!productData.originCountry?.value,
        onError: () => {
            toast(t('Failed to load geolocation data for the selected country.'), {
                type: 'error'
            });
        }
    });

    const handleAreaChange = useCallback(({ action, id, geoJSON, name }) => {
        switch (action) {
            case 'create':
                setGeoLocationData((currentState) => {
                    return {
                        ...currentState,
                        [name]: [
                            ...currentState[name],
                            {
                                id: id,
                                geoJSON: geoJSON
                            }
                        ]
                    }
                })
                break;

            case 'edit':
                setGeoLocationData((currentState) => {
                    const foundIndex = currentState[name].findIndex((item) => item.id === id);

                    const newState = {...currentState};

                    if (foundIndex < 0) {
                        return newState;
                    }

                    newState[name][foundIndex].geoJSON = geoJSON;

                    return newState;
                })
                break;

            case 'delete':
                setGeoLocationData((currentState) => {
                    const newState = {...currentState};

                    newState[name] = currentState[name].filter((item) => item.id !== id);

                    return newState;
                })
                break;
            default:
                break;
        }
    }, []);

    const handleMapLoad = useCallback((loadedItems, name) => {
        setGeoLocationData((currentState) => {
            const newState = { ...currentState };

            newState[name] = loadedItems;

            return newState;
        });
    }, [ ]);

    const plotValidator = useCallback(({ geoJSON }) => {
        return validatePlotMutation({
            ...geoJSON,
            properties: {
                commodity: commodity,
                name: `company-test-${commodity}`,
                production_end: productData.importDate.toISOString().split("T")[0]
            }
        });
    }, [validatePlotMutation, productData]);
    

    const plotValidationSettings = useMemo(() => {
        return {
            enabled: true,
            validate: plotValidator,
            errorMessage: {
                title: t('Invalid Plot Detected'),
                body: (
                    <>
                        {t('The selected plot did not pass the plausibility verification check.')}
                        <br />
                        {t('This means the plot’s boundaries or geolocation data are not consistent with the expected agricultural area or satellite validation criteria.')}
                        <br />
                        {t('Please review the drawn area and ensure it accurately represents a valid production plot before proceeding.')}
                    </>
                )
            }
        }
    }, [plotValidator, t]);

    const boundaryRestrictionSettings = useMemo(() => {
        return {
            enabled: !!originCountryGeoData?.geojson,
            area: originCountryGeoData?.geojson,
            errorMessage: {
                title: t('Outside Country Boundary'),
                body: (
                    <>
                        {t('The selected area is outside the boundary of the chosen country.')}
                        <br />
                        {t('Please draw your plot within the country limits to ensure valid geolocation data.')}
                    </>
                )
            }
        }
    }, [t, originCountryGeoData?.geojson ]);

    const handleSubmit = (e, values) => {
        setProductData((currentState) => {
            return { ...currentState, ...values }
        });

        onNextButtonClicked();
    }

    let displaySkipLabel = true;

    for(const key in geoLocationData) {
        if (geoLocationData[key].length > 0) {
            displaySkipLabel = false;
            break;
        }
    }

    if (!commodity) {
        return (
            <Card>
                <CardBody>
                    <Row>
                        <Col sm='12'>
                            <Alert color="info" className="d-flex align-items-start">
                                <RiErrorWarningLine size={30} className="me-2 mt-1" />
                                <div>
                                    <strong>
                                        {t('Geolocation Data not required')}
                                    </strong>
                                    <br />
                                    {t('This step applies only to products regulated under the EU Deforestation Regulation (EUDR).')}
                                    {t('Since this product is not classified as EUDR, no geolocation or plot data needs to be provided.')}
                                </div>
                            </Alert>
                        </Col>

                        <Col sm='12' className="d-flex justify-content-between">
                            <Button
                                color="secondary"
                                outline
                                type="button"
                                onClick={onCancelButtonClicked}
                            >
                                {t('Cancel')}
                            </Button>

                            <div>
                                <Button
                                    color="secondary"
                                    onClick={onBackButtonClicked}
                                    className="me-2"
                                    type="button"
                                >
                                    {t('Back')}
                                </Button>

                                <Button
                                    color="primary"
                                    type="button"
                                    onClick={onNextButtonClicked}
                                >
                                    {t('Next')}
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </CardBody>
            </Card>
        )
    }

    if (commodity !== 'cattle') {
        return (
            <Card>
                <CardHeader className="bg-white">
                    <h5 className="mb-1">
                        {t('Geolocation Data')}
                    </h5>
                    <p className="text-muted mb-0">
                        {t('Draw the production area of the product on the map. Make sure the plot is within the country of origin. This information is used to validate EUDR compliance and traceability.')}
                    </p>
                </CardHeader>

                <CardBody>
                    <AvForm
                        className="needs-validation m-2"
                        onValidSubmit={handleSubmit}
                    >
                        <Row>
                            <Col sm='12' className="mb-3">
                                <Label>
                                    {t('Geolocation (Draw Polygon)')}
                                </Label>

                                <GeoMap
                                    name="geolocationReference"
                                    onAreaChange={handleAreaChange}
                                    onLoaded={handleMapLoad}
                                    preDefinedGeo={productData?.geolocationReference}
                                    multiDraw={true}
                                    defaultSearchParam={productData.originCountry.label}
                                    boundaryRestriction={boundaryRestrictionSettings}
                                    isLoading={originCountryGeoDataIsFetching}
                                    plotValidation={plotValidationSettings}
                                    styles={{
                                        height: '600px'
                                    }}
                                />

                                <AvField
                                    name="geolocationReference"
                                    type="hidden"
                                    errorMessage={t("This field cannot be blank")}
                                    className="form-control"
                                    validate={{
                                        required: { value: false }
                                    }}
                                    value={(geoLocationData.geolocationReference || []).map((loc) => loc.geoJSON)}
                                />
                            </Col>

                            <Col sm='12' className="d-flex justify-content-between">
                                <Button
                                    color="secondary"
                                    outline
                                    type="button"
                                    onClick={onCancelButtonClicked}
                                >
                                    {t('Cancel')}
                                </Button>

                                <div>
                                    <Button
                                        color="secondary"
                                        onClick={onBackButtonClicked}
                                        className="me-2"
                                        type="button"
                                    >
                                        {t('Back')}
                                    </Button>

                                    <Button
                                        color="primary"
                                        type="submit"
                                    >
                                        {t(displaySkipLabel ? 'Skip' : 'Next')}
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </AvForm>
                </CardBody>
            </Card>
        )
    }

    return (
        <Card>
            <CardHeader className="bg-white">
                <h5 className="mb-1">
                    {t('Geolocation Data')}
                </h5>
                <p className="text-muted mb-0">
                    {t('For cattle products, geolocation data must cover the entire production chain — from the animal’s birth to processing.')}{' '}
                    {t('Please draw the locations for the Breeding Farm, Fattening Farm, and Slaughterhouse on the maps below. Each map should accurately represent the area of activity to ensure full traceability and EUDR compliance.')}
                </p>
            </CardHeader>

            <CardBody>
                <AvForm
                    className="needs-validation m-2"
                    onValidSubmit={handleSubmit}
                >
                    <Row>
                        <Col sm='12' className="mb-3">
                            <Label>
                                {t('Breeding Farm Location')}
                            </Label>

                            <GeoMap
                                name="geolocationReference"
                                onAreaChange={handleAreaChange}
                                onLoaded={handleMapLoad}
                                preDefinedGeo={productData?.geolocationReference}
                                multiDraw={true}
                                defaultSearchParam={productData.originCountry.label}
                                boundaryRestriction={boundaryRestrictionSettings}
                                isLoading={originCountryGeoDataIsFetching}
                                plotValidation={plotValidationSettings}
                                styles={{
                                    height: '400px'
                                }}
                            />

                            <AvField
                                name="geolocationReference"
                                type="hidden"
                                errorMessage={t("This field cannot be blank")}
                                className="form-control"
                                validate={{
                                    required: { value: false }
                                }}
                                value={(geoLocationData.geolocationReference || []).map((loc) => loc.geoJSON)}
                            />
                        </Col>

                        <Col sm='12' className="mb-3">
                            <Label>
                                {t('Fattening Farm Location')}
                            </Label>

                            <GeoMap
                                name="farmGeolocationReference"
                                onAreaChange={handleAreaChange}
                                onLoaded={handleMapLoad}
                                preDefinedGeo={productData?.farmGeolocationReference}
                                multiDraw={true}
                                defaultSearchParam={productData.originCountry.label}
                                boundaryRestriction={boundaryRestrictionSettings}
                                isLoading={originCountryGeoDataIsFetching}
                                plotValidation={plotValidationSettings}
                                styles={{
                                    height: '400px'
                                }}
                            />

                            <AvField
                                name="farmGeolocationReference"
                                type="hidden"
                                errorMessage={t("This field cannot be blank")}
                                className="form-control"
                                validate={{
                                    required: { value: false }
                                }}
                                value={(geoLocationData.farmGeolocationReference || []).map((loc) => loc.geoJSON)}
                            />
                        </Col>

                        <Col sm='12' className="mb-3">
                            <Label>
                                {t('Slaughterhouse Location')}
                            </Label>

                            <GeoMap
                                name="slaughterGeolocationReference"
                                onAreaChange={handleAreaChange}
                                onLoaded={handleMapLoad}
                                preDefinedGeo={productData?.slaughterGeolocationReference}
                                multiDraw={true}
                                defaultSearchParam={productData.originCountry.label}
                                boundaryRestriction={boundaryRestrictionSettings}
                                isLoading={originCountryGeoDataIsFetching}
                                plotValidation={plotValidationSettings}
                                styles={{
                                    height: '400px'
                                }}
                            />

                            <AvField
                                name="slaughterGeolocationReference"
                                type="hidden"
                                errorMessage={t("This field cannot be blank")}
                                className="form-control"
                                validate={{
                                    required: { value: false }
                                }}
                                value={(geoLocationData.slaughterGeolocationReference || []).map((loc) => loc.geoJSON)}
                            />
                        </Col>

                        <Col sm='12' className="d-flex justify-content-between">
                            <Button
                                color="secondary"
                                outline
                                type="button"
                                onClick={onCancelButtonClicked}
                            >
                                {t('Cancel')}
                            </Button>

                            <div>
                                <Button
                                    color="secondary"
                                    onClick={onBackButtonClicked}
                                    className="me-2"
                                    type="button"
                                >
                                    {t('Back')}
                                </Button>

                                <Button
                                    color="primary"
                                    type="submit"
                                >
                                    {t(displaySkipLabel ? 'Skip' : 'Next')}
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </AvForm>
            </CardBody>
        </Card>
    )
}

const mapStatetoProps = (state) => {
    const { token } = state.Login;
    return {
        user: state.Login.user,
        token: token
    };
};

export default withNamespaces()(
    withRouter(connect(
        mapStatetoProps,
        {}
    )(Geolocation))
)